import { mergeRefs } from "@react-aria/utils";

import TableCell from "components/Table/Cell";
import { Certificate, ChevronDown } from "components/icons/generated";
import Icon from "ds/components/Icon";
import Tooltip from "ds/components/Tooltip";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import DropdownMenu from "ds/components/DropdownMenu";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import Typography from "ds/components/Typography";
import useElementWidth from "hooks/useElementWidth";
import useOverflowItems from "hooks/useOverflowItems";

import { LaunchPadTrackUserProgress } from "../types";
import { LAUNCHPAD_TRACK_COLUMN_ID } from "../constants";

const ICON_WIDTH = 16;
const GAP = 4;
const INITIAL_TRIGGER_WIDTH = 56;
const getItemWidth = () => ICON_WIDTH;

type LaunchPadTrackCertificatesCellProps = {
  user: Pick<LaunchPadTrackUserProgress, "certificates">;
};

const LaunchPadTrackCertificatesCell = ({ user }: LaunchPadTrackCertificatesCellProps) => {
  const certificates = user.certificates ?? [];
  const [containerRef, availableWidth] = useElementWidth<HTMLDivElement>();
  const [triggerRef, triggerWidth] = useElementWidth<HTMLButtonElement>(INITIAL_TRIGGER_WIDTH);

  const { visibleItems: visibleCerts, hiddenItems: hiddenCerts } = useOverflowItems({
    items: certificates,
    containerWidth: availableWidth,
    getItemWidth,
    overflowTriggerWidth: triggerWidth,
    gap: GAP,
  });

  return (
    <TableCell id={LAUNCHPAD_TRACK_COLUMN_ID.CERTIFICATES}>
      <Box ref={containerRef} align="center" gap="small" fullWidth>
        {!visibleCerts.length && !hiddenCerts.length && "-"}
        {visibleCerts.map((certificate) => (
          <Tooltip
            key={certificate.id}
            on={(props) => <Icon {...props} src={Certificate} noShrink />}
          >
            {certificate.courseName}
          </Tooltip>
        ))}
        {!!hiddenCerts.length && (
          <DropdownMenu
            triggerComponent={
              <Tooltip
                on={({ ref, ...props }) => (
                  <Button
                    {...props}
                    ref={mergeRefs<HTMLButtonElement>(ref, triggerRef)}
                    aria-label="Show more certificates"
                    variant="secondary"
                    size="small"
                    endIcon={ChevronDown}
                  >
                    +{hiddenCerts.length}
                  </Button>
                )}
              >
                Show more certificates
              </Tooltip>
            }
          >
            {hiddenCerts.map((certificate) => (
              <DropdownMenuItem key={certificate.id}>
                <Box align="center" gap="medium">
                  <Icon src={Certificate} />
                  <Typography variant="p-body3" tag="span">
                    {certificate.courseName}
                  </Typography>
                </Box>
              </DropdownMenuItem>
            ))}
          </DropdownMenu>
        )}
      </Box>
    </TableCell>
  );
};

export default LaunchPadTrackCertificatesCell;
